import React, { useState, useEffect } from 'react'
import { Classes, Intent, DialogProps, Spinner, Tag, SpinnerSize } from '@blueprintjs/core'

// import moment from 'moment'
import {
  Button,
  EventCalendar,
  Flex,
  IconButton,
  PrettyDate,
  Callout,
  prettyDate,
  Toaster,
  Dialog,
} from '@ui'
import api, { IScenarioGroup, IAssignmentPlan } from '@api'
import { redirectTo } from './utils'
import useFormState from './useFormState'
import 'react-day-picker/dist/style.css'
import css from './apWidget.module.scss'
import { DateUtil, TIME } from '@utils'

export function NewPlanGroupDialog({ isOpen, onClose }: DialogProps & { onClose: () => void }) {
  const [date, setDate] = useState<null | Date>(null)
  const { loading, error, onSubmit, onError } = useFormState(isOpen)

  // reset state on dialog close
  useEffect(() => {
    !isOpen && setDate(null)
  }, [isOpen])

  return (
    <Dialog
      {...{
        isOpen,
        onClose,
        canEscapeKeyClose: !loading,
        canOutsideClickClose: !loading,
      }}
      style={{ width: 360, paddingBottom: 20 }}
      usePortal
      body={
        <div className={`${css.dialog_content} ${Classes.DIALOG_BODY}`}>
          <div className={css.dialog_toolbar}>
            <IconButton minimal iconName="times" onClick={onClose} />
          </div>
          {error && (
            <Callout
              intent={Intent.DANGER}
              faIcon="exclamation-triangle"
              style={{ marginBottom: 10 }}>
              <span style={{ color: 'var(--red)' }}>{error.message}</span>
            </Callout>
          )}
          <p style={{ color: 'var(--grey1)', marginBottom: 20 }}>
            Select a date for your next move
          </p>
          <EventCalendar
            city={null}
            showLegend={false}
            notifications={false}
            expirations={false}
            onDateSelect={(newDate: Date) => setDate(newDate)}
            minDate={DateUtil().startOf(TIME.DAYS).toDate()}
          />
          <Button
            loading={loading}
            disabled={!date || loading}
            intent={Intent.PRIMARY}
            style={{ width: '100%', marginTop: 50 }}
            text={
              date ? (
                <>
                  Create{' '}
                  <strong>
                    <PrettyDate value={date} />
                  </strong>
                </>
              ) : (
                'Select a date'
              )
            }
            onClick={() => {
              if (date) {
                onSubmit()
                api.ap
                  .createGroup({
                    deployAt: DateUtil(date).format('yyyy-MM-dd'),
                  })
                  .then(onSuccess)
                  .catch(onError)
              }
            }}
          />
        </div>
      }></Dialog>
  )
}

export function NewPlanDialog({
  group,
  isOpen,
  onClose,
}: DialogProps & { group: IScenarioGroup | null; onClose: () => void }) {
  const { loading, onSubmit } = useFormState(isOpen)

  useEffect(() => {
    if (isOpen && group) {
      api.ap
        .create({ scenarioGroupId: group.id })
        .then(onSuccess)
        .catch((error) => {
          Toaster.fail({ error })
          onClose()
        })
    }
  }, [group, isOpen, onSubmit, onClose])

  return (
    <Dialog
      {...{
        isOpen,
        onClose,
        canEscapeKeyClose: !loading,
        canOutsideClickClose: !loading,
      }}
      usePortal
      style={{ width: 360 }}
      body={
        <div className={`${css.dialog_content} ${Classes.DIALOG_BODY}`} style={{ paddingTop: 14 }}>
          {group && (
            <Flex align="center">
              <Spinner size={SpinnerSize.SMALL} />
              <div style={{ paddingLeft: 10 }}>
                Adding new plan to{' '}
                <strong>
                  <PrettyDate utc value={group.deployAt} />
                </strong>
                ...
              </div>
            </Flex>
          )}
        </div>
      }></Dialog>
  )
}

export function ClonePlanDialog({
  plan,
  isOpen,
  onClose,
}: DialogProps & { plan: IAssignmentPlan | null; onClose: () => void }) {
  const { loading, onSubmit } = useFormState(isOpen)

  useEffect(() => {
    if (isOpen && plan) {
      onSubmit()
      api.ap
        .clone({ id: plan.id })
        .then(onSuccess)
        .catch((error) => {
          Toaster.fail({ error })
          onClose()
        })
    }
  }, [plan, isOpen, onSubmit, onClose])

  return (
    <Dialog
      {...{
        isOpen,
        onClose,
        canEscapeKeyClose: !loading,
        canOutsideClickClose: !loading,
      }}
      style={{ width: 360 }}
      body={
        <div className={`${css.dialog_content} ${Classes.DIALOG_BODY}`} style={{ paddingTop: 14 }}>
          {plan && (
            <Flex align="center">
              <Spinner size={SpinnerSize.SMALL} />
              <div style={{ paddingLeft: 10 }}>
                Duplicating <strong>{plan.alias || 'Plan ' + plan.name}</strong>
                ...
              </div>
            </Flex>
          )}
        </div>
      }
    />
  )
}

export function DeletePlanDialog({
  plan,
  isOpen,
  onClose,
  deletePlan,
}: DialogProps & {
  plan: IAssignmentPlan | null
  onClose: () => void
  deletePlan: (id: string) => void
}) {
  const { loading, error, onSubmit, onError } = useFormState(isOpen)

  return (
    <Dialog
      {...{
        isOpen,
        onClose,
        canEscapeKeyClose: !loading,
        canOutsideClickClose: !loading,
      }}
      style={{ width: 360 }}
      body={
        <div className={Classes.DIALOG_BODY}>
          {error && (
            <Callout
              intent={Intent.DANGER}
              faIcon="exclamation-triangle"
              style={{ margin: '20px 0 10px 0' }}>
              <span style={{ color: 'var(--red)' }}>{error.message}</span>
            </Callout>
          )}
          {plan && (
            <>
              <h3>Delete {plan.alias || 'Plan ' + plan.name}</h3>
              <p>This will remove all planned moves and allocation changes.</p>
            </>
          )}
        </div>
      }
      footer={
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button disabled={loading} text="Cancel" onClick={onClose} />
            <Button
              autoFocus
              loading={loading}
              intent={Intent.DANGER}
              text="Delete"
              onClick={() => {
                if (plan) {
                  onSubmit()
                  api.ap
                    .remove({ id: plan.id })
                    .then(() => {
                      Toaster.success({
                        message: `${plan.alias || 'Plan ' + plan.name} deleted`,
                      })
                      onClose()
                      deletePlan(plan.id)
                    })
                    .catch(onError)
                }
              }}
            />
          </div>
        </div>
      }></Dialog>
  )
}

export function PromotePlanDialog({
  plan,
  isOpen,
  onClose,
}: DialogProps & {
  plan: IAssignmentPlan | null
}) {
  const { loading, error, onSubmit, onError } = useFormState(isOpen)

  return (
    <Dialog
      {...{
        isOpen,
        onClose: () => onclose,
        canEscapeKeyClose: !loading,
        canOutsideClickClose: !loading,
      }}
      style={{ width: 360 }}
      body={
        <div className={Classes.DIALOG_BODY}>
          {error && (
            <Callout
              intent={Intent.DANGER}
              faIcon="exclamation-triangle"
              style={{ margin: '20px 0 10px 0' }}>
              <span style={{ color: 'var(--red)' }}>{error.message}</span>
            </Callout>
          )}
          {plan && (
            <>
              <h3>Commit to {plan.alias || 'Plan ' + plan.name}</h3>
              <p style={{ marginBottom: 20 }}>
                When committed, this plan will affect all future plans. Other plans for this date
                will be deleted.
              </p>
            </>
          )}
        </div>
      }
      footer={
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button disabled={loading} text="Cancel" onClick={onClose} />
            <Button
              autoFocus
              loading={loading}
              intent={Intent.PRIMARY}
              text="Commit"
              onClick={() => {
                if (plan) {
                  onSubmit()
                  api.ap.promote({ id: plan.id }).then(onSuccess).catch(onError)
                }
              }}
            />
          </div>
        </div>
      }
    />
  )
}

export function DeletePlanGroupDialog({
  group,
  isOpen,
  onClose,
  deleteGroup,
}: DialogProps & {
  onClose: () => void
  group: IScenarioGroup | null
  deleteGroup: (id: string) => void
}) {
  const { loading, error, onSubmit, onError } = useFormState(isOpen)

  return (
    <Dialog
      {...{
        isOpen,
        onClose,
        canEscapeKeyClose: !loading,
        canOutsideClickClose: !loading,
      }}
      style={{ width: 360 }}
      body={
        <div className={Classes.DIALOG_BODY}>
          {error && (
            <Callout
              intent={Intent.DANGER}
              faIcon="exclamation-triangle"
              style={{ margin: '20px 0 10px 0' }}>
              <span style={{ color: 'var(--red)' }}>{error.message}</span>
            </Callout>
          )}
          {group && (
            <>
              <h3>Delete {prettyDate({ value: group.deployAt, utc: true })}</h3>
              <p style={{ marginBottom: 20 }}>
                This will remove all plans for{' '}
                <Tag minimal round>
                  {prettyDate({ value: group.deployAt, utc: true })}
                </Tag>
              </p>
            </>
          )}
        </div>
      }
      footer={
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button disabled={loading} text="Cancel" onClick={onClose} />
            <Button
              autoFocus
              loading={loading}
              intent={Intent.DANGER}
              text="Delete"
              onClick={() => {
                if (group) {
                  onSubmit()
                  api.ap
                    .removeGroup({ id: group.id })
                    .then(() => {
                      Toaster.success({
                        message: `All plans for ${prettyDate({
                          value: group.deployAt,
                          utc: true,
                        })} deleted`,
                      })
                      onClose()
                      deleteGroup(group.id)
                    })
                    .catch(onError)
                }
              }}
            />
          </div>
        </div>
      }></Dialog>
  )
}

function onSuccess(item: IAssignmentPlan | IScenarioGroup) {
  let message: string
  let plan: IAssignmentPlan

  if ('assignmentPlans' in item) {
    message = `Plan ${item.assignmentPlans[0].name} added to ${prettyDate({
      value: item.deployAt,
      utc: true,
    })}`
    plan = item.assignmentPlans[0]
  } else {
    message = `${item.alias || 'Plan ' + item.name} added to ${prettyDate({
      value: item.deployAt,
      utc: true,
    })}`
    plan = item
  }

  Toaster.success({ message })
  // Allow toaster animation before redirecting to new plan
  setTimeout(() => redirectTo(plan), 1000)
}
